import { Product3DAssetReference, Product3DReference } from "./product3DTypes";

/**
 * Drop `footprintSvg` from a catalog 3D asset before placing it on a symbol or
 * writing the plan to disk. The SVG is only needed at insert time to compute
 * `symbol.svgPath`; after that the plan re-fetches it from the catalog when
 * hydrating. Persisting the same ~70KB string on every copy of a stool is what
 * ballooned Lionbar-scale plans to ~11MB (≈43% of that file was this field).
 */
export function omitProduct3DFootprintSvg<
  T extends Product3DAssetReference | undefined,
>(asset: T): T {
  if (!asset || asset.footprintSvg === undefined) return asset;
  const { footprintSvg: _omit, ...rest } = asset;
  return rest as T;
}

export function selectHydratableProduct3DAsset(
  product: Product3DReference | undefined,
  readyAssets: Product3DAssetReference[],
): Product3DAssetReference | undefined {
  if (!product?.productId || readyAssets.length === 0) return undefined;
  const matchingProductAssets = readyAssets.filter(
    (asset) =>
      asset.status === "ready" &&
      Boolean(asset.glbUrl) &&
      (!asset.productId || asset.productId === product.productId),
  );
  if (matchingProductAssets.length === 0) return undefined;

  const currentAssetId = product.asset?.id;
  if (currentAssetId) {
    const currentAsset = matchingProductAssets.find(
      (asset) => asset.id === currentAssetId,
    );
    if (currentAsset) return currentAsset;
  }

  const variantAsset = product.variantId
    ? matchingProductAssets.find(
        (asset) => asset.variantId === product.variantId,
      )
    : undefined;
  return variantAsset ?? matchingProductAssets[0];
}

export function hydrateProduct3DReference(
  product: Product3DReference,
  asset: Product3DAssetReference,
): Product3DReference {
  return {
    ...product,
    renderMode:
      product.renderMode === "billboardPreview"
        ? product.renderMode
        : "asset3D",
    asset: omitProduct3DFootprintSvg({
      ...product.asset,
      ...asset,
    }),
    dimensions: product.dimensions ?? asset.dimensions,
    dimensionConfidence:
      product.dimensionConfidence ??
      (asset.dimensions ? "inferred" : product.dimensionConfidence),
    dimensionUnit:
      product.dimensionUnit ?? (asset.dimensions ? "m" : undefined),
    dimensionSource:
      product.dimensionSource ??
      (asset.dimensions ? "product3DAsset" : product.dimensionSource),
    fallbackReason: undefined,
  };
}

export function shouldHydrateProduct3DReference(
  product: Product3DReference | undefined,
  asset: Product3DAssetReference | undefined,
): product is Product3DReference {
  if (!product || !asset) return false;
  if (product.asset?.id !== asset.id) return true;
  if (product.asset?.glbUrl !== asset.glbUrl) return true;
  if (product.asset?.thumbnailUrl !== asset.thumbnailUrl) return true;
  if (product.asset?.quality?.sha256 !== asset.quality?.sha256) return true;
  // Strip legacy persisted footprintSvg from memory even when URLs are current.
  if (product.asset?.footprintSvg !== undefined) return true;
  return false;
}
